import React, { useState, useEffect } from 'react';
import { 
  Database, 
  Upload, 
  Search,
  Trash2,
  Edit2,
  ExternalLink,
  Clock,
  FileText,
  TrendingUp,
  Brain,
  Pause,
  Play,
  WifiOff,
  AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import BigDataDatasetService, { BigDataDataset } from '../../services/BigDataDatasetService';
import BigDataUploader from './BigDataUploader';
import { DatasetMetadata } from '../../lib/duckdb-engine';
import { useNavigate } from 'react-router-dom';
import { debugLog } from '../../utils/debug-logger';
import logger from '../../utils/logger';
import { useUploadSession } from '../../hooks/useUploadSession';

interface BigDataHomeProps {
  onSelectDataset: (datasetId: string, dataset: BigDataDataset) => void;
}

const BigDataHome: React.FC<BigDataHomeProps> = ({ onSelectDataset }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [datasets, setDatasets] = useState<BigDataDataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploader, setShowUploader] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Realtime upload progress
  const { activeUploads, hasActiveUploads } = useUploadSession({ userId: user?.id });

  useEffect(() => {
    loadDatasets();
  }, [user]);

  const loadDatasets = async () => {
    if (!user) return;
    
    debugLog('[🏠 BigDataHome] loadDatasets - Starting...');
    setLoading(true);
    try {
      const data = await BigDataDatasetService.getUserDatasets(user.id);
      debugLog('[📊 BigDataHome] loadDatasets - Received datasets:', data.length);
      
      // Log chaque dataset pour debug
      data.forEach((d, index) => {
        debugLog(`[🔍 BigDataHome] Dataset ${index + 1}:`, {
          id: d.id,
          file_name: d.file_name,
          status: d.status,
          row_count: d.row_count,
          column_count: d.column_count,
          hasColumns: !!d.columns,
          columnsType: typeof d.columns,
          columnsIsArray: Array.isArray(d.columns),
          columnsLength: d.columns?.length
        });
      });
      
      setDatasets(data);
      debugLog('[✅ BigDataHome] loadDatasets - Datasets set in state');
    } catch (error) {
      logger.error('[❌ BigDataHome] Failed to load datasets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadComplete = (metadata: DatasetMetadata) => {
    debugLog('[🎉 BigDataHome] Upload complete!');
    debugLog('[📦 BigDataHome] Metadata received:', metadata);
    debugLog('[🔍 BigDataHome] Metadata.columns:', {
      hasColumns: !!metadata.columns,
      columnsType: typeof metadata.columns,
      columnsIsArray: Array.isArray(metadata.columns),
      columnsLength: metadata.columns?.length
    });
    
    setShowUploader(false);
    loadDatasets();
  };

  const handleDelete = async (datasetId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Supprimer ce dataset et tout son historique ?')) return;
    if (!user) return;

    try {
      await BigDataDatasetService.deleteDataset(datasetId, user.id);
      setDatasets(datasets.filter(d => d.id !== datasetId));
    } catch (error) {
      logger.error('[BigDataHome] Delete failed:', error);
      alert('Échec de la suppression');
    }
  };

  const filteredDatasets = datasets.filter(d =>
    d.file_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (showUploader) {
    return (
      <div className="p-8">
        <button
          onClick={() => setShowUploader(false)}
          className="mb-6 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-white transition-colors"
        >
          ← Retour
        </button>
        <BigDataUploader onUploadComplete={handleUploadComplete} onError={console.error} />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2 flex items-center space-x-3">
          <Brain className="w-10 h-10 text-purple-500" />
          <span>Big Data Analytics</span>
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Uploadez vos fichiers massifs et laissez l'IA les analyser de manière autonome
        </p>
      </div>

      {/* ═══════ Pending uploads banner ═══════ */}
      {hasActiveUploads && (
        <div className="mb-6 space-y-3">
          {activeUploads.map((upload) => {
            const pct = upload.totalChunks && upload.totalChunks > 0
              ? Math.round(((upload.uploadedChunks || 0) / upload.totalChunks) * 100)
              : 0;
            const isPaused = upload.uploadStatus === 'paused';

            return (
              <div
                key={upload.id}
                onClick={() => setShowUploader(true)}
                className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 cursor-pointer hover:bg-amber-500/15 transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    {isPaused ? (
                      <Pause className="w-4 h-4 text-amber-500" />
                    ) : (
                      <Upload className="w-4 h-4 text-amber-500 animate-pulse" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-theme-primary truncate">
                      {isPaused ? 'Upload en pause' : 'Upload en cours'} — {upload.fileName}
                    </p>
                    <p className="text-xs text-theme-secondary">
                      {upload.uploadedChunks}/{upload.totalChunks} chunks — {pct}%
                      {upload.uploadSpeed ? ` — ${(upload.uploadSpeed / (1024 * 1024)).toFixed(1)} MB/s` : ''}
                    </p>
                  </div>
                  <span className="text-xs text-amber-600 dark:text-amber-400 font-medium whitespace-nowrap">
                    Cliquez pour voir
                  </span>
                </div>
                <div className="relative h-1.5 bg-amber-500/10 rounded-full overflow-hidden">
                  <div
                    className={`absolute inset-y-0 left-0 rounded-full transition-all ${
                      isPaused ? 'bg-amber-500' : 'bg-amber-500 animate-pulse'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Actions Bar */}
      <div className="mb-6 flex items-center space-x-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600 dark:text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un dataset..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-theme-surface border border-theme text-theme-primary placeholder-theme-tertiary"
          />
        </div>
        <button
          onClick={() => setShowUploader(true)}
          className="btn-modern btn-primary inline-flex items-center space-x-2"
        >
          <Upload className="w-5 h-5" />
          <span>Nouveau fichier</span>
        </button>
      </div>

      {/* Datasets Grid */}
      {loading ? (
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Chargement des datasets...</p>
        </div>
      ) : filteredDatasets.length === 0 ? (
        <div className="text-center py-20 border border-theme bg-theme-surface">
          <p className="font-display text-xl mb-2">
            {searchQuery ? 'Aucun résultat' : 'Aucun dataset'}
          </p>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {searchQuery 
              ? 'Essayez une autre recherche'
              : 'Commencez par uploader votre premier fichier'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => setShowUploader(true)}
              className="btn-modern btn-primary inline-flex items-center space-x-2"
            >
              <Upload className="w-5 h-5" />
              <span>Uploader un fichier</span>
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDatasets.map((dataset) => (
            <div
              key={dataset.id}
              onClick={() => onSelectDataset(dataset.id, dataset)}
              className="border border-theme bg-theme-surface p-6 hover:border-[var(--theme-text-primary)] cursor-pointer group"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 truncate group-hover:text-purple-400 transition-colors">
                    {dataset.file_name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {formatSize(dataset.file_size)}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => handleDelete(dataset.id, e)}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-400 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Lignes</span>
                  <span className="font-mono text-gray-900 dark:text-white">
                    {dataset.row_count?.toLocaleString() || '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Colonnes</span>
                  <span className="font-mono text-gray-900 dark:text-white">
                    {dataset.column_count || '-'}
                  </span>
                </div>
              </div>

              {/* Status */}
              <div className="mb-4">
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${
                    dataset.status === 'completed' ? 'bg-green-500' :
                    dataset.status === 'processing' ? 'bg-yellow-500 animate-pulse' :
                    'bg-red-500'
                  }`} />
                  <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                    {dataset.status === 'completed' ? 'Prêt' :
                     dataset.status === 'processing' ? 'En cours...' :
                     'Erreur'}
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="pt-4 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                <div className="flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>{formatDate(dataset.created_at)}</span>
                </div>
                <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BigDataHome;

